import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

export default function TheMarket() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-6">
      <SlideHeader eyebrow="The ASO Market">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-5xl md:leading-[1.1]">
          1M+ publishers. €239M serviceable today.
        </h1>
        <div className="mt-4 flex flex-wrap items-stretch md:mt-4">
          <ChevronStep first>B2B SaaS</ChevronStep>
          <ChevronStep>MarTech</ChevronStep>
          <ChevronStep>Mobile Apps &amp; Games</ChevronStep>
          <ChevronStep>ASO</ChevronStep>
          <ChevronStep last terminal>
            Constant ASO Platform
          </ChevronStep>
        </div>
      </SlideHeader>

      <div className="flex flex-col md:min-h-0 md:flex-1 md:justify-center">
        {/* Funnel — TAM → SAM → SOM, narrowing left to right */}
        <div className="flex flex-col items-stretch gap-3 md:flex-row md:gap-0">
        {/* TAM */}
        <FunnelCard tier="TAM" sub="Top-down · category">
          <div className="flex flex-1 flex-col justify-center">
            <div className="flex items-end gap-3">
              <Money label="today" value="$0.5–1.0B" />
              <span className="pb-1 text-xl text-slate-300 md:text-2xl">→</span>
              <Money label="by 2035" value="$1.9–7.6B" accent />
            </div>
            <p className="mt-2 text-sm font-bold text-blue-700 md:text-base">
              12.5–20.7%{" "}
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 md:text-xs">
                CAGR
              </span>
            </p>
          </div>
          <p className="mt-3 text-[11px] leading-snug text-slate-500 md:text-xs">
            Four independent ASO-software reports.
            <SupLink to="sources">8,9,10,11</SupLink> Scope
            <SupLink to="sources">12</SupLink>
          </p>
        </FunnelCard>

        <Connector />

        {/* SAM — emphasized */}
        <FunnelCard tier="SAM" sub="Bottom-up · serviceable" emphasized>
          <p className="text-3xl font-bold tracking-tight text-blue-700 md:text-[2.75rem] md:leading-none">
            €239M
          </p>
          <div className="mt-3 flex flex-1 flex-col justify-center gap-2 md:mt-4">
            <SamRow
              seg="Publishers"
              math="100K × €184/mo × 12"
              value="€221M"
            />
            <SamRow
              seg="Agencies & pros"
              math="~3K × €500/mo × 12"
              value="€18M"
            />
          </div>
        </FunnelCard>

        <Connector />

        {/* SOM */}
        <FunnelCard tier="SOM" sub="Our path · publisher share">
          <div className="flex flex-1 flex-col justify-center gap-1.5">
            <SomRung pct="0.7% · 700 accts" value="€1.55M" note="m12 target" />
            <SomRung pct="5% · 5K accts" value="€11M" />
            <SomRung pct="20% · 20K accts" value="€44M" />
          </div>
          <p className="mt-3 text-[11px] leading-snug text-slate-500 md:text-xs">
            End-of-period run-rate ARR at increasing penetration.
          </p>
        </FunnelCard>
      </div>

      {/* Proof strip — the population & category are real */}
      <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 border-t border-slate-200 pt-4 md:mt-6 md:grid-cols-3 md:pt-5">
        <Proof value="1M+" tint="slate" label="Active publishers globally">
          848K App Store · 615K Play
          <SupLink to="sources">1</SupLink>, less ≈30% cross-publishing overlap
        </Proof>
        <Proof value="5K+" tint="slate" label="ASO specialists & agencies">
          ~400 agencies (Phiture, Gummicube, Yodel) · ~3K consultants
          <SupLink to="sources">14</SupLink>
        </Proof>
        <Proof value="$1B+" tint="blue" label="Category M&amp;A + VC since 2020">
          Sensor Tower ← data.ai (2024) · AppTweak Series B · AppFollow VC-backed
          <SupLink to="sources">13</SupLink>
        </Proof>
      </div>

      {/* Adjacent-TAM inoculation — the Wider Play companion line (A7Y-1926) */}
      <p className="mt-4 text-center text-sm italic leading-snug text-slate-600 md:mt-5 md:text-base">
        ASO is the beachhead, not the ceiling: adjacent app-marketing tooling is
        a $5–15B layer inside $109B/yr of mobile app marketing spend — the wider
        play follows.
      </p>
      </div>
    </div>
  );
}

/* ---------- Funnel ---------- */

function FunnelCard({
  tier,
  sub,
  emphasized,
  children,
}: {
  tier: string;
  sub: string;
  emphasized?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex flex-1 flex-col rounded-2xl p-4 ring-1 md:p-5 ${
        emphasized
          ? "bg-blue-50 ring-blue-200"
          : "bg-white ring-slate-200"
      }`}
    >
      <div className="mb-3 md:mb-4">
        <p className="text-xl font-bold tracking-tight text-blue-700 md:text-2xl">
          {tier}
        </p>
        <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500 md:text-[11px]">
          {sub}
        </p>
      </div>
      {children}
    </div>
  );
}

function Connector() {
  return (
    <div className="flex items-center justify-center py-1 md:px-2 md:py-0">
      <span className="text-xl text-slate-300 md:text-2xl">→</span>
    </div>
  );
}

function Money({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div>
      <p className="text-[11px] text-slate-500 md:text-xs">{label}</p>
      <p
        className={`text-xl font-bold tracking-tight md:text-2xl ${
          accent ? "text-blue-700" : "text-slate-900"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function SamRow({
  seg,
  math,
  value,
}: {
  seg: string;
  math: string;
  value: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-t border-blue-100 pt-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{seg}</p>
        <p className="text-[11px] leading-tight text-slate-500 md:text-xs">
          {math}
        </p>
      </div>
      <span className="shrink-0 text-base font-bold tabular-nums text-slate-900 md:text-lg">
        {value}
      </span>
    </div>
  );
}

function SomRung({
  pct,
  value,
  note,
}: {
  pct: string;
  value: string;
  note?: string;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-2 rounded-lg px-2.5 py-1.5 ${
        note ? "bg-blue-50 ring-1 ring-blue-200" : ""
      }`}
    >
      <span className="text-sm text-slate-700">
        {pct}
        {note ? (
          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            {note}
          </span>
        ) : null}
      </span>
      <span
        className={`shrink-0 text-base font-bold tabular-nums md:text-lg ${
          note ? "text-blue-700" : "text-slate-900"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/* ---------- Proof strip ---------- */

function Proof({
  value,
  label,
  tint,
  children,
}: {
  value: string;
  label: string;
  tint: "slate" | "blue";
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-baseline gap-2.5">
        <p
          className={`text-2xl font-bold tracking-tight md:text-3xl ${
            tint === "blue" ? "text-blue-700" : "text-slate-900"
          }`}
        >
          {value}
        </p>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-700 md:text-[13px]">
          {label}
        </p>
      </div>
      <p className="mt-1 text-xs leading-snug text-slate-500 md:text-sm">
        {children}
      </p>
    </div>
  );
}

/* ---------- Taxonomy chevron ladder ---------- */

function ChevronStep({
  children,
  first,
  last,
  terminal,
}: {
  children: ReactNode;
  first?: boolean;
  last?: boolean;
  terminal?: boolean;
}) {
  const padding = first
    ? "py-1.5 pl-3 pr-5 md:py-2.5 md:pl-5 md:pr-8"
    : last
    ? "py-1.5 pl-5 pr-3 md:py-2.5 md:pl-8 md:pr-5"
    : "py-1.5 pl-5 pr-5 md:py-2.5 md:pl-8 md:pr-8";

  // -ml is set to POINT - 2 so a 2px hairline gap reveals the chevron seam
  const overlap = first ? "" : "-ml-[8px] md:-ml-[12px]";

  const color = terminal
    ? "bg-blue-100 font-semibold text-blue-700"
    : "bg-slate-100 text-slate-700";

  // POINT = 10px (mobile), 14px (md). first = flat left + point right.
  // last = notch left + flat right. middle = notch left + point right.
  const clipMobile = first
    ? "[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%)]"
    : last
    ? "[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,10px_50%)]"
    : "[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%,10px_50%)]";

  const clipMd = first
    ? "md:[clip-path:polygon(0_0,calc(100%-14px)_0,100%_50%,calc(100%-14px)_100%,0_100%)]"
    : last
    ? "md:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,14px_50%)]"
    : "md:[clip-path:polygon(0_0,calc(100%-14px)_0,100%_50%,calc(100%-14px)_100%,0_100%,14px_50%)]";

  return (
    <div
      className={`flex items-center whitespace-nowrap text-sm md:text-lg ${padding} ${overlap} ${color} ${clipMobile} ${clipMd}`}
    >
      {children}
    </div>
  );
}
